import { z } from 'zod';
import { maKySchema } from './ky.js';
import { thucNhanSchema } from './thucNhan.js';
/**
 * Chia lợi nhuận kênh cho đội ngũ: nhóm kênh, trưởng nhóm, quản lý, editor.
 *
 * Chuỗi tính của một kỳ, mỗi mũi tên là một bước hiện trên giao diện:
 *
 * ```
 * Doanh thu gộp → thuế Mỹ → phần Network → VAT → Thực nhận   ← revenue.tinhThucNhanKenh
 *   → Chi phí vận hành → Lợi nhuận kênh                       ← ở đây
 *   → Trưởng nhóm / Quản lý / Editor → Công ty giữ            ← ở đây
 * ```
 *
 * **Ba vai trò hưởng độc lập**: % của mỗi dòng tính trên *cùng* một cơ sở, không
 * phải lấy phần còn lại của dòng trước. Một người giữ hai vai trò thì có hai
 * dòng và hưởng cộng dồn.
 *
 * Mọi phép tính làm trên **cent (số nguyên)**, làm tròn nửa lên ở từng dòng —
 * cùng luật với `thucNhan.ts`. Phần lẻ sau khi làm tròn các dòng **thuộc công
 * ty**, vì công ty giữ phần còn lại chứ không nhận một tỷ lệ riêng.
 */
/* ------------------------------------------------------------------ */
/* Hằng số và enum                                                     */
/* ------------------------------------------------------------------ */
/** Vai trò được hưởng chia. Thứ tự này cũng là thứ tự hiện trên bảng. */
export const VAI_TRO_CHIA = ['truong_nhom', 'quan_ly', 'editor'];
export const vaiTroChiaSchema = z.enum(VAI_TRO_CHIA);
export const VAI_TRO_CHIA_LABEL = {
    truong_nhom: 'Trưởng nhóm',
    quan_ly: 'Quản lý kênh',
    editor: 'Editor',
};
/** Vai trò đặt ở **kênh** (trưởng nhóm đặt ở nhóm). */
export const VAI_TRO_KENH = ['quan_ly', 'editor'];
export const vaiTroKenhSchema = z.enum(VAI_TRO_KENH);
/** Hình thức tính tiền của một dòng chia. */
export const HINH_THUC_CHIA = ['pct', 'co_dinh', 'ca_hai'];
export const hinhThucChiaSchema = z.enum(HINH_THUC_CHIA);
export const HINH_THUC_CHIA_LABEL = {
    pct: 'Phần trăm',
    co_dinh: 'Cố định mỗi tháng',
    ca_hai: 'Phần trăm + cố định',
};
/**
 * Cách xử lý khi **kênh lỗ** (lợi nhuận ≤ 0). Đặt ở nhóm, kênh ghi đè được.
 *
 * - `chia_theo_thuc_nhan` (mặc định): vẫn chia, nhưng % tính trên **thực nhận**
 *   (trước chi phí vận hành) — công ty chịu phần chi phí. Thực nhận ≤ 0 thì
 *   phần chia bằng 0.
 * - `khong_chia`: kỳ lỗ không ai được chia.
 * - `cong_don_lo`: kỳ lỗ không chia, số lỗ **mang sang** kỳ sau và trừ vào lợi
 *   nhuận kỳ sau trước khi chia.
 */
export const CACH_XU_LY_KENH_LO = ['chia_theo_thuc_nhan', 'khong_chia', 'cong_don_lo'];
export const cachXuLyKenhLoSchema = z.enum(CACH_XU_LY_KENH_LO);
export const CACH_XU_LY_KENH_LO_LABEL = {
    chia_theo_thuc_nhan: 'Vẫn chia, tính trên thực nhận',
    khong_chia: 'Kỳ lỗ không chia',
    cong_don_lo: 'Cộng dồn lỗ sang kỳ sau',
};
export const MAC_DINH_CACH_XU_LY_KENH_LO = 'chia_theo_thuc_nhan';
/** Loại khoản chi. `dau_tu_ban_dau` **không bao giờ** trừ vào lợi nhuận. */
export const LOAI_CHI_PHI = [
    'cong_cu',
    'nhac',
    'thue_ngoai',
    'quang_cao',
    'dau_tu_ban_dau',
    'khac',
];
export const loaiChiPhiSchema = z.enum(LOAI_CHI_PHI);
export const LOAI_CHI_PHI_LABEL = {
    cong_cu: 'Công cụ',
    nhac: 'Nhạc',
    thue_ngoai: 'Thuê ngoài',
    quang_cao: 'Quảng cáo',
    dau_tu_ban_dau: 'Đầu tư ban đầu',
    khac: 'Khác',
};
/** Tổng % một kênh vượt mức này thì **cảnh báo** (vẫn lưu được). */
export const NGUONG_CANH_BAO_PCT = 50;
/** Tổng % một kênh vượt mức này thì **chặn**, không lưu. */
export const NGUONG_CHAN_PCT = 100;
/* ------------------------------------------------------------------ */
/* Số học trên cent                                                    */
/* ------------------------------------------------------------------ */
/** Tiền → cent (số nguyên), làm tròn nửa lên. */
function sangCent(tien) {
    return Math.round(tien * 100);
}
/** Cent → tiền hai số lẻ. */
function sangTien(cent) {
    return Math.round(cent) / 100;
}
/**
 * `tu / duoi` làm tròn **nửa lên**, cả hai là số nguyên không âm.
 *
 * Không dùng `Math.round(tu / duoi)`: phép chia dấu phẩy động có thể ra
 * `…,49999999` ở đúng mốc nửa và làm tròn xuống. `(2·tu + duoi) / (2·duoi)` giữ
 * mọi thứ trong số nguyên tới bước cuối.
 */
function chiaNuaLen(tu, duoi) {
    if (duoi === 0)
        return 0;
    return Math.floor((2 * tu + duoi) / (2 * duoi));
}
/* ------------------------------------------------------------------ */
/* Dòng chia                                                           */
/* ------------------------------------------------------------------ */
export const dongChiaDauVaoSchema = z.object({
    /** Id dòng tỷ lệ (bản ghi trong `team_leader_shares` / `channel_staff_shares`). */
    khoa: z.string(),
    userId: z.string(),
    tenNguoi: z.string(),
    vaiTro: vaiTroChiaSchema,
    hinhThuc: hinhThucChiaSchema,
    /** 0–100. Bỏ qua khi `hinhThuc = 'co_dinh'`. */
    pct: z.number().min(0).max(100),
    /** USD mỗi tháng. Bỏ qua khi `hinhThuc = 'pct'`. */
    coDinhUsd: z.number().min(0),
    /**
     * Số ngày dòng này **có hiệu lực trong kỳ**. Bằng `soNgayKy` khi dòng phủ cả
     * kỳ; nhỏ hơn khi tỷ lệ đổi giữa kỳ (xem `soNgayHieuLucTrongKy`).
     */
    soNgayHieuLuc: z.number().int().nonnegative(),
});
export const dongChiaKetQuaSchema = dongChiaDauVaoSchema.extend({
    /** Phần tính theo %, đã chia theo số ngày hiệu lực. */
    phanTheoPct: z.number(),
    /** Phần cố định, đã chia theo số ngày hiệu lực. */
    phanCoDinh: z.number(),
    /** Tổng của dòng, làm tròn 2 số lẻ. */
    tien: z.number(),
    /** `soNgayHieuLuc / soNgayKy`, để giao diện ghi "15/31 ngày". */
    soNgayKy: z.number().int().positive(),
    /** `true` khi dòng không phủ hết kỳ (tỷ lệ đổi giữa kỳ). */
    chiaTheoNgay: z.boolean(),
});
export const buocChiaSchema = z.object({
    khoa: z.string(),
    nhan: z.string(),
    giaTri: z.number(),
    /** `tong` = dòng cộng dồn, `tru` = dòng bị trừ, `cuoi` = kết quả. */
    kieu: z.enum(['tong', 'tru', 'cuoi']),
    ghiChu: z.string().nullable(),
});
export const ketQuaChiaKenhSchema = z.object({
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    /** Thực nhận − chi phí vận hành. Âm được. */
    loiNhuan: z.number(),
    /** Số mà mọi % nhân vào. Bằng lợi nhuận, hoặc thực nhận khi kênh lỗ. */
    coSoChia: z.number(),
    /** `true` khi lợi nhuận ≤ 0. */
    kenhLo: z.boolean(),
    /**
     * `true` khi kênh **chưa khai** `operationStartDate`. Chi phí vẫn trừ đủ (xem
     * `chiPhiTinhVaoVanHanh`), nhưng khoản phát sinh trước lúc kênh bắt đầu đăng
     * video **chưa được tách**, nên con số còn phải sửa lại sau khi nhập ngày.
     */
    thieuNgayVanHanh: z.boolean(),
    /** `true` khi đang lấy **thực nhận** làm cơ sở vì kênh lỗ. */
    chiaTheoThucNhan: z.boolean(),
    chinhSach: cachXuLyKenhLoSchema,
    /** Lỗ mang sang từ kỳ trước (chỉ chính sách `cong_don_lo`). */
    loMangSang: z.number(),
    /** Lỗ mang sang **sau** kỳ này. */
    loMangSangSau: z.number(),
    dong: z.array(dongChiaKetQuaSchema),
    tongChia: z.number(),
    /** Lợi nhuận − tổng chia. Âm khi kênh lỗ mà vẫn chia. */
    congTyGiu: z.number(),
    /** Tổng % của mọi dòng (kể cả trưởng nhóm), chưa chia theo ngày. */
    tongPct: z.number(),
    canhBaoVuotPct: z.boolean(),
    buoc: z.array(buocChiaSchema),
});
const DINH_DANG_TIEN = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
function tienVn(x) {
    return `${x.toLocaleString('vi-VN', DINH_DANG_TIEN)} US$`;
}
function pctVn(x) {
    return `${x.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;
}
/**
 * Chia lợi nhuận một kênh trong một kỳ.
 *
 * Công thức mỗi dòng, khi dòng phủ cả kỳ:
 *
 * ```
 * tiền = round2(cơ sở × %) + cố định
 * ```
 *
 * Khi tỷ lệ **đổi giữa kỳ**, dòng chỉ hiệu lực `n` trong `N` ngày của kỳ nên cả
 * hai phần chia theo ngày — làm tròn **một lần** ở cuối mỗi phần:
 *
 * ```
 * tiền = round2(cơ sở × n/N × %) + round2(cố định × n/N)
 * ```
 *
 * Ví dụ kỳ 31 ngày, quản lý 10% tới hết 15/10 rồi đổi 12%: hai dòng, một dòng
 * 15 ngày 10% và một dòng 16 ngày 12%.
 */
export function tinhChiaKenh(dau) {
    const chinhSach = dau.chinhSach ?? MAC_DINH_CACH_XU_LY_KENH_LO;
    const soNgayKy = Math.max(1, Math.trunc(dau.soNgayKy));
    const thieuNgay = dau.thieuNgayVanHanh ?? false;
    const thucNhanCent = sangCent(dau.thucNhan);
    const chiPhiCent = sangCent(dau.chiPhiVanHanh);
    const loiNhuanCent = thucNhanCent - chiPhiCent;
    const loMangSangCent = Math.max(0, sangCent(dau.loMangSang ?? 0));
    const kenhLo = loiNhuanCent <= 0;
    /*
     * Cơ sở chia và phần lỗ mang sang kỳ sau.
     *
     * `cong_don_lo` là chính sách duy nhất nhìn tới kỳ khác: kỳ có lãi phải bù lỗ
     * cũ trước, kỳ lỗ thì cộng thêm vào số mang sang.
     */
    let coSoCent = 0;
    let loSauCent = loMangSangCent;
    let chiaTheoThucNhan = false;
    if (chinhSach === 'cong_don_lo') {
        if (loiNhuanCent > 0) {
            coSoCent = Math.max(0, loiNhuanCent - loMangSangCent);
            loSauCent = Math.max(0, loMangSangCent - loiNhuanCent);
        }
        else {
            coSoCent = 0;
            loSauCent = loMangSangCent - loiNhuanCent;
        }
    }
    else if (loiNhuanCent > 0) {
        coSoCent = loiNhuanCent;
    }
    else if (chinhSach === 'chia_theo_thuc_nhan') {
        coSoCent = Math.max(0, thucNhanCent);
        chiaTheoThucNhan = coSoCent > 0;
    }
    const dong = dau.dong.map((d) => {
        const soNgay = Math.min(Math.max(0, Math.trunc(d.soNgayHieuLuc)), soNgayKy);
        const coPct = d.hinhThuc !== 'co_dinh';
        const coCoDinh = d.hinhThuc !== 'pct';
        /*
         * Cơ sở bằng 0 thì **cả dòng** bằng 0, kể cả phần cố định: quy tắc "thực
         * nhận ≤ 0 → phần chia 0" và chính sách `khong_chia` nói về *phần chia* của
         * người đó, không riêng phần tính theo %.
         */
        const pctCent = coPct && coSoCent > 0
            ? chiaNuaLen(coSoCent * soNgay * Math.round(d.pct * 100), soNgayKy * 10_000)
            : 0;
        const coDinhCent = coCoDinh && coSoCent > 0 ? chiaNuaLen(sangCent(d.coDinhUsd) * soNgay, soNgayKy) : 0;
        return {
            ...d,
            soNgayHieuLuc: soNgay,
            phanTheoPct: sangTien(pctCent),
            phanCoDinh: sangTien(coDinhCent),
            tien: sangTien(pctCent + coDinhCent),
            soNgayKy,
            chiaTheoNgay: soNgay < soNgayKy,
        };
    });
    const tongPct = dau.dong.reduce((s, d) => s + (d.hinhThuc === 'co_dinh' ? 0 : d.pct), 0);
    return dungKetQuaChia({
        thucNhan: sangTien(thucNhanCent),
        chiPhiVanHanh: sangTien(chiPhiCent),
        loiNhuan: sangTien(loiNhuanCent),
        coSoChia: sangTien(coSoCent),
        kenhLo,
        chiaTheoThucNhan,
        chinhSach,
        loMangSang: sangTien(loMangSangCent),
        loMangSangSau: sangTien(loSauCent),
        dong,
        soNgayKy,
        thieuNgayVanHanh: thieuNgay,
        tongPct,
    });
}
/**
 * Dựng `KetQuaChiaKenh` từ **số đã có**, không tính lại đồng nào.
 *
 * Tách khỏi {@link tinhChiaKenh} vì có chỗ gọi đã có sẵn tiền của từng dòng và
 * **không được phép** tính lại: kỳ lăn gồm nhiều đoạn (xem
 * `team.chiaTheoKenh`), trong đó đoạn trọn tháng đã chốt lấy tiền từ ảnh chụp.
 * Nhân lại % trên cơ sở chia của cả kỳ sẽ cho ra một con số khác với số đã đem
 * đi trả người ta.
 */
export function dungKetQuaChia(dau) {
    const { thucNhan, chiPhiVanHanh, loiNhuan, coSoChia, kenhLo, chiaTheoThucNhan, chinhSach, loMangSang, loMangSangSau, dong, soNgayKy, thieuNgayVanHanh: thieuNgay, tongPct, } = dau;
    const thucNhanCent = sangCent(thucNhan);
    const chiPhiCent = sangCent(chiPhiVanHanh);
    const loiNhuanCent = sangCent(loiNhuan);
    const coSoCent = sangCent(coSoChia);
    const loMangSangCent = sangCent(loMangSang);
    const loSauCent = sangCent(loMangSangSau);
    const tongChiaCent = dong.reduce((s, d) => s + sangCent(d.tien), 0);
    const congTyCent = loiNhuanCent - tongChiaCent;
    const buoc = [
        {
            khoa: 'thucNhan',
            nhan: 'Công ty thực nhận',
            giaTri: sangTien(thucNhanCent),
            kieu: 'tong',
            ghiChu: 'sau thuế Mỹ, phần Network và VAT',
        },
        {
            khoa: 'chiPhiVanHanh',
            nhan: 'Chi phí vận hành',
            giaTri: -sangTien(chiPhiCent),
            kieu: 'tru',
            /*
             * Nói đúng luật đang áp. Chưa khai ngày vận hành thì mọi khoản không phải
             * đầu tư ban đầu đều bị trừ, kể cả khoản tiêu trước khi kênh đăng video —
             * dòng ghi chú phải nói ra, nếu không người đọc tưởng số đã tách rồi.
             */
            ghiChu: thieuNgay
                ? 'chưa đặt ngày bắt đầu vận hành — trừ mọi khoản không phải đầu tư ban đầu'
                : 'chỉ các khoản từ ngày bắt đầu vận hành, không gồm đầu tư ban đầu',
        },
        {
            khoa: 'loiNhuan',
            nhan: 'Lợi nhuận kênh',
            giaTri: sangTien(loiNhuanCent),
            kieu: 'tong',
            ghiChu: kenhLo ? 'kênh lỗ trong kỳ' : null,
        },
    ];
    if (chinhSach === 'cong_don_lo' && loMangSangCent > 0) {
        buoc.push({
            khoa: 'loMangSang',
            nhan: 'Lỗ mang sang từ kỳ trước',
            giaTri: -sangTien(loMangSangCent),
            kieu: 'tru',
            ghiChu: null,
        });
    }
    if (coSoCent !== loiNhuanCent) {
        buoc.push({
            khoa: 'coSoChia',
            nhan: 'Cơ sở chia',
            giaTri: sangTien(coSoCent),
            kieu: 'tong',
            ghiChu: chiaTheoThucNhan
                ? 'kênh lỗ — chia theo thực nhận, công ty chịu chi phí'
                : CACH_XU_LY_KENH_LO_LABEL[chinhSach],
        });
    }
    for (const d of dong) {
        const phan = [];
        if (d.hinhThuc !== 'co_dinh')
            phan.push(pctVn(d.pct));
        if (d.hinhThuc !== 'pct')
            phan.push(`${tienVn(d.coDinhUsd)}/tháng`);
        if (d.chiaTheoNgay)
            phan.push(`${String(d.soNgayHieuLuc)}/${String(soNgayKy)} ngày`);
        buoc.push({
            khoa: `dong:${d.khoa}`,
            nhan: `${VAI_TRO_CHIA_LABEL[d.vaiTro]} · ${d.tenNguoi}`,
            giaTri: -d.tien,
            kieu: 'tru',
            ghiChu: phan.join(' · '),
        });
    }
    buoc.push({
        khoa: 'congTyGiu',
        nhan: 'Công ty giữ',
        giaTri: sangTien(congTyCent),
        kieu: 'cuoi',
        ghiChu: congTyCent < 0 ? 'công ty bù lỗ kỳ này' : null,
    });
    return {
        thucNhan: sangTien(thucNhanCent),
        chiPhiVanHanh: sangTien(chiPhiCent),
        loiNhuan: sangTien(loiNhuanCent),
        coSoChia: sangTien(coSoCent),
        kenhLo,
        thieuNgayVanHanh: thieuNgay,
        chiaTheoThucNhan,
        chinhSach,
        loMangSang: sangTien(loMangSangCent),
        loMangSangSau: sangTien(loSauCent),
        dong,
        tongChia: sangTien(tongChiaCent),
        congTyGiu: sangTien(congTyCent),
        tongPct: Math.round(tongPct * 100) / 100,
        canhBaoVuotPct: tongPct > NGUONG_CANH_BAO_PCT,
        buoc,
    };
}
/* ------------------------------------------------------------------ */
/* Hiệu lực theo ngày                                                  */
/* ------------------------------------------------------------------ */
const KHOA_NGAY = /^\d{4}-\d{2}-\d{2}$/;
/** `YYYY-MM-DD` → số ngày kể từ mốc, để đếm ngày mà không qua `Date` múi giờ. */
function soThuTuNgay(khoa) {
    const [y, m, d] = khoa.split('-').map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}
/**
 * Số ngày một khoảng hiệu lực `[tu, den]` giao với kỳ `[kyTu, kyDen]`.
 *
 * Cả hai đầu **tính cả ngày đầu và ngày cuối**: một dòng hiệu lực đúng ngày
 * 05/10 trong kỳ tháng 10 là 1 ngày, không phải 0. `den = null` nghĩa là còn
 * hiệu lực vô hạn.
 */
export function soNgayHieuLucTrongKy(tu, den, kyTu, kyDen) {
    if (!KHOA_NGAY.test(kyTu) || !KHOA_NGAY.test(kyDen))
        return 0;
    const a = tu && KHOA_NGAY.test(tu) ? Math.max(soThuTuNgay(tu), soThuTuNgay(kyTu)) : soThuTuNgay(kyTu);
    const b = den && KHOA_NGAY.test(den)
        ? Math.min(soThuTuNgay(den), soThuTuNgay(kyDen))
        : soThuTuNgay(kyDen);
    return Math.max(0, b - a + 1);
}
/** Số ngày của kỳ `[kyTu, kyDen]`, tính cả hai đầu. */
export function soNgayKy(kyTu, kyDen) {
    return soNgayHieuLucTrongKy(null, null, kyTu, kyDen);
}
/**
 * Khoản chi có bị trừ vào lợi nhuận hay không — **nơi duy nhất** quyết định
 * việc này. Mọi màn hình (thẻ tổng của tab, bảng chi phí, khung "Cách chia kỳ",
 * bốn cột ở Quản lý kênh, trang Đối soát nhân sự) đi qua đúng hàm này rồi qua
 * đúng một lượt {@link tinhChiaKenh}; không chỗ nào tự cộng lại.
 *
 * Hai trường hợp **không** trừ: loại `dau_tu_ban_dau`, và khoản phát sinh
 * **trước ngày bắt đầu vận hành** (ngày kênh bắt đầu đăng video).
 *
 * ⚠️ **Chưa khai ngày vận hành thì vẫn trừ** mọi khoản không phải
 * `dau_tu_ban_dau`. Trước đây hàm này trả `false` cho cả cụm — "chưa vận hành
 * thì chưa có chi phí" — nhưng hệ quả là một kênh đã tiêu tiền thật lại hiện
 * *chi phí vận hành 0* và *công ty giữ* cao hơn thực tế, im lặng, chỉ vì quản
 * trị chưa kịp nhập một cái ngày. Số sai mà không ai thấy thì tệ hơn số thiếu
 * tách: nay mặc định là **trừ**, và giao diện hiện cảnh báo vàng kèm nút nhận
 * ngày gợi ý để quản trị tách lại cho đúng. Trang Đối soát còn **chặn chốt**
 * người có kênh chưa khai ngày.
 */
export function chiPhiTinhVaoVanHanh(x) {
    if (x.loai === 'dau_tu_ban_dau')
        return false;
    if (!KHOA_NGAY.test(x.ngayPhatSinh))
        return false;
    if (!x.ngayBatDauVanHanh || !KHOA_NGAY.test(x.ngayBatDauVanHanh))
        return true;
    return x.ngayPhatSinh >= x.ngayBatDauVanHanh;
}
/* ------------------------------------------------------------------ */
/* Kiểm tổng phần trăm                                                 */
/* ------------------------------------------------------------------ */
export const ketQuaKiemPctSchema = z.object({
    tong: z.number(),
    canhBao: z.boolean(),
    chan: z.boolean(),
    thongBao: z.string().nullable(),
});
/**
 * Tổng % của một kênh — **gồm cả trưởng nhóm**.
 *
 * Vượt {@link NGUONG_CANH_BAO_PCT} thì cảnh báo nhưng vẫn lưu; vượt
 * {@link NGUONG_CHAN_PCT} thì chặn. Dòng `co_dinh` không góp % nào.
 */
export function kiemTongPct(dong) {
    const tongRaw = dong.reduce((s, d) => s + (d.hinhThuc === 'co_dinh' ? 0 : d.pct), 0);
    const tong = Math.round(tongRaw * 100) / 100;
    const chan = tong > NGUONG_CHAN_PCT;
    const canhBao = tong > NGUONG_CANH_BAO_PCT;
    return {
        tong,
        canhBao,
        chan,
        thongBao: chan
            ? `Tổng tỷ lệ của kênh là ${pctVn(tong)}, vượt ${pctVn(NGUONG_CHAN_PCT)} — không lưu được.`
            : canhBao
                ? `Tổng tỷ lệ của kênh là ${pctVn(tong)}, vượt ${pctVn(NGUONG_CANH_BAO_PCT)}.`
                : null,
    };
}
/* ------------------------------------------------------------------ */
/* DTO của màn hình                                                    */
/* ------------------------------------------------------------------ */
/** Một dòng tỷ lệ như giao diện thấy (có hiệu lực hoặc đã thay). */
export const dongTyLeSchema = z.object({
    id: z.string(),
    userId: z.string(),
    tenNguoi: z.string(),
    emailNguoi: z.string(),
    nguoiDaLuuTru: z.boolean(),
    vaiTro: vaiTroChiaSchema,
    hinhThuc: hinhThucChiaSchema,
    pct: z.number(),
    coDinhUsd: z.number(),
    hieuLucTu: z.string(),
    hieuLucDen: z.string().nullable(),
    ghiChu: z.string().nullable(),
    /** `false` = dòng đã bị thay, giao diện hiện mờ kèm nhãn "đã thay". */
    dangHieuLuc: z.boolean(),
    /** Nhóm cấp dòng này; `null` với dòng đặt ở kênh. */
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
});
export const khoanChiSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    ngayPhatSinh: z.string(),
    period: z.string(),
    khoan: z.string(),
    loai: loaiChiPhiSchema,
    lapHangThang: z.boolean(),
    usd: z.number(),
    /**
     * Phần của khoản này **rơi vào kỳ đang xem**, chỉ có khi kỳ là một khoảng
     * ngày không trùng trọn tháng (xem `chiPhiTrongKhoang` của module 10).
     *
     * Khoản `lapHangThang` phủ cả tháng, nên khoảng cắt ngang tháng chỉ gánh
     * phần theo số ngày: 120 $/tháng × 10/31 ngày = 38,71 $. Để riêng một cột
     * thay vì sửa `usd` vì `usd` là **số tiền thật trên chứng từ** — sửa nó thì
     * bảng chi phí không còn đối chiếu được với hoá đơn.
     */
    usdTrongKy: z.number().optional(),
    tepChungTu: z.string().nullable(),
    nguoiNhap: z.string().nullable(),
    /** Có bị trừ vào lợi nhuận kỳ này hay không (xem `chiPhiTinhVaoVanHanh`). */
    tinhVaoVanHanh: z.boolean(),
    ghiChu: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const nhomSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    color: z.string(),
    note: z.string().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    /** Chính sách kênh lỗ của nhóm. */
    chinhSachKenhLo: cachXuLyKenhLoSchema,
    /** Quản lý kênh có được tự nhập chi phí kênh mình hay không. */
    quanLyNhapChiPhi: z.boolean(),
    soKenh: z.number().int().nonnegative(),
    /** Id các kênh đang thuộc nhóm ở kỳ đang xem (để mở hộp chọn kênh). */
    channelIds: z.array(z.string()),
    truongNhom: z.array(dongTyLeSchema),
    /** Mọi người có mặt trong nhóm (trưởng nhóm + đội ngũ các kênh). */
    thanhVien: z.array(z.object({
        userId: z.string(),
        tenNguoi: z.string(),
        vaiTro: z.array(vaiTroChiaSchema),
        soKenh: z.number().int().nonnegative(),
    })),
});
/** Kết quả chia của một kênh trong một kỳ, kèm chuỗi thực nhận để diễn giải. */
export const chiaKyKenhSchema = z.object({
    channelId: z.string(),
    tenKenh: z.string(),
    period: z.string(),
    kyTu: z.string(),
    kyDen: z.string(),
    soNgayKy: z.number().int().positive(),
    ngayBatDauVanHanh: z.string().nullable(),
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
    /** Chuỗi doanh thu gộp → thực nhận, lấy từ module revenue. */
    thucNhanChiTiet: thucNhanSchema.nullable(),
    chiPhi: z.array(khoanChiSchema),
    /** Tổng các khoản `dau_tu_ban_dau` và khoản trước ngày vận hành. */
    dauTuBanDau: z.number(),
    ketQua: ketQuaChiaKenhSchema,
    /**
     * `true` khi người đang xem **không** được thấy tiền của người khác: `dong`
     * chỉ còn dòng của chính họ, `congTyGiu`/`thucNhan`/`loiNhuan` trả 0 và
     * `thucNhanChiTiet` trả `null`. Quyết định ở server, không phải ở giao diện.
     */
    daChe: z.boolean(),
    /**
     * Số kỳ đã lùi để tính lỗ mang sang (chính sách `cong_don_lo`). Chạm trần
     * `SO_KY_LUI_TOI_DA` thì giao diện nói rõ "tính từ N kỳ gần nhất".
     */
    soKyLui: z.number().int().nonnegative(),
    /**
     * `true` khi kỳ là một **khoảng ngày tự do** (28 ngày qua, 01/09–15/10…)
     * chứ không phải một tháng tròn.
     *
     * Hai hệ quả, giao diện phải nói ra:
     *
     * - số là **tạm tính**: sao kê Network, biên bản chia và đối soát nhân sự
     *   đều chốt theo tháng, nên con số của một khoảng không khớp chứng từ nào;
     * - **lỗ cộng dồn bị bỏ qua** (`loMangSang = 0`, `soKyLui = 0`): lỗ mang
     *   sang đếm theo *kỳ tháng*, không có nghĩa với một khoảng cắt ngang.
     *
     * Khoảng trùng **đúng** một tháng không bật cờ này: nó đi lại đường của kỳ
     * tháng (kể cả ảnh chụp lúc chốt) nên ra đúng số đối soát.
     */
    theoKhoang: z.boolean(),
});
/** Trần số kỳ lùi lại khi tính lỗ cộng dồn (chính sách `cong_don_lo`). */
export const SO_KY_LUI_TOI_DA = 6;
/** Dữ liệu của tab "Đội ngũ & chia sẻ" trên trang kênh. */
export const doiNguKenhSchema = z.object({
    channelId: z.string(),
    tenKenh: z.string(),
    ngayBatDauVanHanh: z.string().nullable(),
    /**
     * Gợi ý ngày bắt đầu vận hành = ngày video đầu tiên, `null` khi module `sync`
     * chưa có video nào. Chỉ là **gợi ý**: quản trị vẫn phải bấm nhận.
     */
    goiYNgayVanHanh: z.string().nullable(),
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
    /** Dòng tỷ lệ của trưởng nhóm (từ nhóm đang phủ kỳ). */
    truongNhom: z.array(dongTyLeSchema),
    /** Quản lý và editor của kênh, **gồm cả dòng đã thay** để hiện mờ. */
    dong: z.array(dongTyLeSchema),
    chinhSachKenhLo: cachXuLyKenhLoSchema,
    /** `true` khi kênh có bản ghi đè riêng, `false` khi đang theo nhóm. */
    chinhSachGhiDe: z.boolean(),
    quanLyNhapChiPhi: z.boolean(),
    /** Người đang xem có được nhập chi phí kênh này hay không (kiểm ở server). */
    duocNhapChiPhi: z.boolean(),
    /** Người đang xem có được sửa nhóm/tỷ lệ/ngày vận hành hay không. */
    duocSuaTyLe: z.boolean(),
    tongPct: ketQuaKiemPctSchema,
    chiaKy: chiaKyKenhSchema,
    /** Các khoản "Đầu tư ban đầu" — không tính vào chia, có tổng riêng. */
    khoanDauTu: z.array(khoanChiSchema),
});
/* ------------------------------------------------------------------ */
/* Input của endpoint                                                  */
/* ------------------------------------------------------------------ */
const khoaNgaySchema = z.string().regex(KHOA_NGAY, 'Ngày phải dạng YYYY-MM-DD');
export const luuNhomSchema = z.object({
    name: z.string().trim().min(1).max(80),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
    note: z.string().trim().max(500).nullish(),
    chinhSachKenhLo: cachXuLyKenhLoSchema.optional(),
    quanLyNhapChiPhi: z.boolean().optional(),
});
export const datKenhNhomSchema = z.object({
    channelIds: z.array(z.string()),
    /** Mặc định hôm nay (giờ Việt Nam). */
    hieuLucTu: khoaNgaySchema.optional(),
});
/**
 * Lưu một dòng tỷ lệ. **Luôn là thêm dòng mới**: dòng cũ của cùng người + cùng
 * vai trò bị đóng ở ngày liền trước `hieuLucTu`, không ghi đè (quy tắc 6).
 */
export const luuTyLeSchema = z
    .object({
    userId: z.string().min(1),
    vaiTro: vaiTroChiaSchema,
    hinhThuc: hinhThucChiaSchema,
    pct: z.number().min(0).max(100).optional(),
    coDinhUsd: z.number().min(0).optional(),
    hieuLucTu: khoaNgaySchema.optional(),
    hieuLucDen: khoaNgaySchema.nullish(),
    ghiChu: z.string().trim().max(500).nullish(),
})
    .refine((x) => x.hinhThuc === 'co_dinh' || (x.pct ?? 0) > 0, {
    message: 'Hình thức có phần trăm thì phải nhập tỷ lệ lớn hơn 0',
    path: ['pct'],
})
    .refine((x) => x.hinhThuc === 'pct' || (x.coDinhUsd ?? 0) > 0, {
    message: 'Hình thức có phần cố định thì phải nhập số tiền lớn hơn 0',
    path: ['coDinhUsd'],
});
export const dongTyLeTuNgaySchema = z.object({
    /** Ngày **cuối cùng** còn hiệu lực. */
    hieuLucDen: khoaNgaySchema,
});
export const luuKhoanChiSchema = z.object({
    ngayPhatSinh: khoaNgaySchema,
    khoan: z.string().trim().min(1).max(160),
    loai: loaiChiPhiSchema,
    lapHangThang: z.boolean().optional(),
    usd: z.number().min(0),
    /** Tên file chứng từ đã tải lên; app chỉ lưu đường dẫn. */
    tepChungTu: z.string().trim().max(400).nullish(),
    ghiChu: z.string().trim().max(500).nullish(),
});
export const luuNgayVanHanhSchema = z.object({
    ngayBatDauVanHanh: khoaNgaySchema.nullable(),
});
export const luuChinhSachKenhSchema = z.object({
    /** `null` = bỏ ghi đè, dùng lại chính sách của nhóm. */
    chinhSachKenhLo: cachXuLyKenhLoSchema.nullable(),
});
/**
 * Kỳ của hai route đọc theo kênh (`/kenh/:id/{doi-ngu,chia-ky}`).
 *
 * Hai cách khai, chọn một:
 *
 * - `period=YYYY-MM` — một tháng, là kỳ tiền của công ty (mặc định);
 * - `from` + `to` — một **khoảng ngày** bất kỳ, cho ô "Kỳ của cả trang" khi
 *   người dùng chọn 28 ngày qua, một năm, toàn bộ thời gian hay khoảng tự
 *   chọn. Phải có **cả hai**, và `from <= to`.
 */
export const chiaKyQuerySchema = z
    .object({
    /** Kỳ `YYYY-MM`. Thiếu cả `ky` lẫn `from`/`to` thì lấy kỳ hiện tại. */
    period: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .optional(),
    /**
     * **Mã kỳ** của ô chọn kỳ chung (`@qlk/shared/ky.ts`) — tên tham số dùng
     * chung của cả app. Máy chủ tự quy ra: mã tháng đi đường kỳ tháng (có ảnh
     * chụp lúc chốt và lỗ cộng dồn), mã khác đi đường khoảng ngày.
     */
    ky: maKySchema.optional(),
    from: khoaNgaySchema.optional(),
    to: khoaNgaySchema.optional(),
})
    .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'Khoảng ngày phải có cả from và to',
})
    .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'Ngày bắt đầu phải trước ngày kết thúc',
});
//# sourceMappingURL=chiaSe.js.map